import { onMounted, ref } from 'vue';
import { libraryMarks, markLibraryItemOpened, saveLibraryItem, unsaveLibraryItem } from '../api/portal';
import { reportError } from '../faro';

// Writes still in flight, across every copy: a page's load waits for them, or
// its read could land before a write the person just made on the other page.
const inflight = new Set();
const track = (p) => {
  inflight.add(p);
  const done = () => inflight.delete(p);
  p.then(done, done);
  return p;
};

/**
 * A policyholder's own marks on Library files (XN-4560): Saved, and opened
 * ("New" until then). The list and a file's page each hold their own copy,
 * loaded on arrival. A star flips at once; the request follows, one at a time
 * per file, and the last tap wins. A failed request puts the star back to what
 * the server holds and says so.
 */
export default function useLibraryMarks() {
  const state = ref('loading'); // loading | ready | error | refused
  const saved = ref(new Set());
  const opened = ref(new Set());

  async function load() {
    state.value = 'loading';
    try {
      await Promise.allSettled([...inflight]);
      const marks = await libraryMarks();
      saved.value = new Set((marks?.saved ?? []).map(Number));
      opened.value = new Set((marks?.opened ?? []).map(Number));
      state.value = 'ready';
    } catch (e) {
      // Staff previewing the portal are refused /my/* by design.
      if (e?.response?.status === 403) {
        state.value = 'refused';
        return;
      }
      reportError(e);
      state.value = 'error';
    }
  }

  const put = (set, id, on) => {
    const next = new Set(set.value);
    if (on) next.add(id); else next.delete(id);
    set.value = next;
  };

  const isSaved = (id) => saved.value.has(Number(id));
  // Without the marks nothing is called new: it would claim everything is.
  const isNew = (id) => state.value === 'ready' && !opened.value.has(Number(id));

  // Per file, while a request is in flight: what the person last asked for,
  // and what the server holds.
  const wanted = new Map();
  const held = new Map();

  function setSaved(id, on) {
    return track(writeSaved(id, on));
  }

  async function writeSaved(id, on) {
    if (!wanted.has(id)) held.set(id, isSaved(id));
    put(saved, id, on);
    const busy = wanted.has(id);
    wanted.set(id, on);
    if (busy) return;

    let sent;
    try {
      while (wanted.get(id) !== held.get(id)) {
        sent = wanted.get(id);
        await (sent ? saveLibraryItem(id) : unsaveLibraryItem(id));
        held.set(id, sent);
      }
    } catch (e) {
      put(saved, id, held.get(id));
      window.loadNotification?.({
        message: sent ? "Couldn't save that. Try again." : "Couldn't remove that from your saved items. Try again.",
        error: true,
      });
    } finally {
      wanted.delete(id);
      held.delete(id);
    }
  }

  const toggle = (rawId) => setSaved(Number(rawId), !isSaved(rawId));

  const sentOpened = new Set();
  function markOpened(rawId) {
    return track(writeOpened(rawId));
  }

  async function writeOpened(rawId) {
    const id = Number(rawId);
    if (state.value === 'refused' || opened.value.has(id) || sentOpened.has(id)) return;
    sentOpened.add(id);
    try {
      await markLibraryItemOpened(id);
      put(opened, id, true);
    } catch (e) {
      // Gone (404) or staff (403) is not a fault: a stale list can lead here.
      if (![403, 404].includes(e?.response?.status)) reportError(e);
    }
  }

  onMounted(load);

  return { state, load, isSaved, isNew, toggle, markOpened };
}
